import { Injectable } from "@angular/core";
import { concatMap, from, map, mergeMap, Observable, of, tap, toArray } from "rxjs";
import { SubjectModel } from "../../../_objects/subject-model";
import { SubjectModelSchema } from "../../../_offline/models/subjectModel.schema";
import { IMAGE_FILETYPE } from "../../../app.constants";
import { FileSystemAccessService } from "../../file-system-access.service";
import { SubjectModelService } from "../../http-api/subject-model.service";
import { toSubjectModelDto } from "../sync-mapper";
import { OnPremiseSyncDataState } from "./on-premise-sync-state.service";
import { InspectionDbService } from "../../database/inspection-db.service";

@Injectable({
  providedIn: 'root'
})
export class InspectionTemplateOnPremiseSyncService {

  constructor(
    private subjectModelService: SubjectModelService,
    private fileSystemAccessService: FileSystemAccessService,
    private inspectionDbService: InspectionDbService,
    private syncState: OnPremiseSyncDataState,
  ) { }

  public createInspectionTemplates(): Observable<SubjectModel[]> {
    return this.getUsedSubjectModels()
      .pipe(
        concatMap(models => models),
        mergeMap(model => this.postInspectionTemplate(model)),
        toArray(),
      );
  }

  private getUsedSubjectModels(): Observable<SubjectModelSchema[]> {
    // We only want to migrate subject models that are used by local inspections.
    return this.inspectionDbService.getAllOnPremise()
      .pipe(
        map(inspections => inspections.map(i => i.subject.subjectModel)),
        map(subjectModels => this.uniqueIds(subjectModels))
      );
  }

  private uniqueIds(items: SubjectModelSchema[]): SubjectModelSchema[] {
    const seen = new Set<string>();
    return items.filter(item => !seen.has(item.id) && seen.add(item.id));
  }

  private postInspectionTemplate(subjectModelSchema: SubjectModelSchema): Observable<SubjectModel> {
    return of(subjectModelSchema).pipe(
      map(toSubjectModelDto),
      mergeMap(model => this.subjectModelService.addSubjectModel(model)),
      tap(model => this.syncState.updateInspectionTemplate(subjectModelSchema.id, model.id)),
      mergeMap(model => this.postInspectionTemplateImage(model, subjectModelSchema.structImageFilePath)),
    );
  }

  private postInspectionTemplateImage(model: SubjectModel, imageFilePath: string) {
    if (!imageFilePath) {
      return of(model);
    }
    return from(this.fileSystemAccessService.getFile(imageFilePath, 'template.jpg', IMAGE_FILETYPE.JPEG)).pipe(
      mergeMap(file => this.subjectModelService.updateSubjectModelImage(model.id, file)),
      map(_ => model)
    );
  }
}
